import React from "react"
import { graphql } from "gatsby"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import CenteredHero from "@/components/CenteredHero"
import IntegrationHubCards from "@/components/IntegrationHubCards"
import IntegrationHubCard from "@/components/IntegrationHubCard"
import SplitPane from "@/components/SplitPane"
import IntegrationCallout from "@/components/IntegrationCallout"

import IntegrationsShowcase from "@/components/IntegrationsShowcase"
import Accordion from "@/components/Accordion"
import AccordionItem from "@/components/AccordionItem"
import TryFreeCallToAction from "@/components/TryFreeCallToAction"

import contactHistoryVideo from "@/videos/contact-history.mp4"
import taskListVideo from "@/videos/task-list.mp4"
import opportunityMilestoneVideo from "@/videos/opportunity-milestone.mp4"
import salesAnalyticsReports from "@/videos/sales-analytics-reports.mp4"
import googleWorkspaceSyncVideo from "@/videos/google-workspace-sync.mp4"

import {
  Link,
  useTranslation,
  Trans,
} from "@zestia/gatsby-plugin-react-i18next"

import useHeadTranslation from "@/hooks/use-head-translation"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("google-workspace-integration.meta-title")}
      description={t("google-workspace-integration.meta-description")}
    />
  )
}

const GoogleWorkspaceIntegrationsPage = ({ data }) => {
  const { t } = useTranslation()

  return (
    <MainLayout
      trackingData={{
        intent: "bofu",
      }}
    >
      <CenteredHero
        centered="true"
        title={t("google-workspace-integration.header.title")}
        text={t("google-workspace-integration.header.description")}
        showButton="true"
        buttonClass="btn btn--lg "
        buttonText={t("google-workspace-integration.header.button-text")}
        backgroundClass="bg-grey-1 pt-16 pb-24"
      />

      <IntegrationHubCards>
        <IntegrationHubCard
          image={{
            src: "gmail.svg",
            alt: t(
              "google-workspace-integration.header-cards.item-1.image-alt",
            ),
          }}
          title={t("google-workspace-integration.header-cards.item-1.title")}
          description={t(
            "google-workspace-integration.header-cards.item-1.description",
          )}
          link="/integrations/gmail/"
          linkText={t(
            "google-workspace-integration.header-cards.item-1.link-text",
          )}
        />
        <IntegrationHubCard
          image={{
            src: "google.svg",
            alt: t(
              "google-workspace-integration.header-cards.item-2.image-alt",
            ),
          }}
          title={t("google-workspace-integration.header-cards.item-2.title")}
          description={t(
            "google-workspace-integration.header-cards.item-2.description",
          )}
          link="/integrations/google-workspace-single-sign-on/"
          linkText={t(
            "google-workspace-integration.header-cards.item-2.link-text",
          )}
        />
      </IntegrationHubCards>

      <SplitPane
        assetAlignment="stacked"
        title={t(
          "google-workspace-integration.more-powerful-google-workspace.title",
        )}
        text={t(
          "google-workspace-integration.more-powerful-google-workspace.description",
        )}
      />

      <SplitPane
        assetAlignment="left"
        video={contactHistoryVideo}
        title={t(
          "google-workspace-integration.view-of-clients-and-projects.title",
        )}
        text={t(
          "google-workspace-integration.view-of-clients-and-projects.description",
        )}
        link={{
          url: "/features/contact-management-software/",
          text: t("google-workspace-integration.manage-to-do-list.link-text"),
        }}
      >
        <IntegrationCallout
          icons={[
            { src: "gmail.svg", alt: "Gmail" },
            { src: "google-contacts.svg", alt: "Google Contacts" },
          ]}
        >
          <span className="pl-4">
            <Trans
              i18nKey="google-workspace-integration.view-of-clients-and-projects.integration-callout"
              components={{
                Gmail: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/gmail/"
                  />
                ),
                GoogleContacts: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/google-contacts/"
                  />
                ),
              }}
            />
          </span>
        </IntegrationCallout>
      </SplitPane>

      <SplitPane
        video={taskListVideo}
        assetAlignment="right"
        title={t("google-workspace-integration.manage-to-do-list.title")}
        text={t("google-workspace-integration.manage-to-do-list.description")}
        link={{
          url: "/features/tasks-and-calendar/",
          text: t("google-workspace-integration.manage-to-do-list.link-text"),
        }}
      >
        <IntegrationCallout
          icons={[
            { src: "gmail.svg", alt: "Gmail" },
            { src: "google-calendar.svg", alt: "Google Calendar" },
          ]}
        >
          <span className="pl-4">
            <Trans
              i18nKey="google-workspace-integration.manage-to-do-list.integration-callout"
              components={{
                Gmail: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/gmail/"
                  />
                ),
                GoogleCalendar: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/google-calendar/"
                  />
                ),
              }}
            />
          </span>
        </IntegrationCallout>
      </SplitPane>

      <SplitPane
        assetAlignment="left"
        video={opportunityMilestoneVideo}
        title={t("google-workspace-integration.manage-sales-pipeline.title")}
        text={t(
          "google-workspace-integration.manage-sales-pipeline.description",
        )}
        link={{
          url: "/features/sales-pipeline/",
          text: t(
            "google-workspace-integration.manage-sales-pipeline.link-text",
          ),
        }}
      >
        <IntegrationCallout icons={[{ src: "gmail.svg", alt: "Gmail" }]}>
          <span className="pl-4">
            <Trans
              i18nKey="google-workspace-integration.manage-sales-pipeline.integration-callout"
              components={{
                Gmail: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/gmail/"
                  />
                ),
              }}
            />
          </span>
        </IntegrationCallout>
      </SplitPane>

      <SplitPane
        assetAlignment="right"
        video={salesAnalyticsReports}
        title={t("google-workspace-integration.make-informed-decisions.title")}
        text={t(
          "google-workspace-integration.make-informed-decisions.description",
        )}
        link={{
          url: "/features/sales-analytics/",
          text: t(
            "google-workspace-integration.make-informed-decisions.link-text",
          ),
        }}
      >
        <IntegrationCallout
          icons={[{ src: "google-looker-studio.svg", alt: "Looker Studio" }]}
        >
          <span className="pl-4">
            <Trans
              i18nKey="google-workspace-integration.make-informed-decisions.integration-callout"
              components={{
                LookerStudioLink: (
                  <Link
                    className="text-link text-link--bare"
                    to="/integrations/looker-studio-connector-crm/"
                  />
                ),
              }}
            />
          </span>
        </IntegrationCallout>
      </SplitPane>

      <SplitPane
        assetAlignment="left"
        video={googleWorkspaceSyncVideo}
        title={t("google-workspace-integration.organize-files-and-data.title")}
        text={t(
          "google-workspace-integration.organize-files-and-data.description",
        )}
      ></SplitPane>

      <IntegrationsShowcase
        title={t("common.integration-highlight-title-default")}
        description={t(
          "google-workspace-integration.do-even-more-with-integrations.description",
        )}
      />

      <Accordion
        title={t("google-workspace-integration.google-accordion.title")}
        description={t(
          "google-workspace-integration.google-accordion.description",
        )}
      >
        <AccordionItem
          title={t(
            "google-workspace-integration.google-accordion.itemOne.title",
          )}
        >
          <p>
            {t("google-workspace-integration.google-accordion.itemOne.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.google-accordion.itemTwo.title",
          )}
        >
          <p>
            {t("google-workspace-integration.google-accordion.itemTwo.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.google-accordion.itemThree.title",
          )}
        >
          <p>
            {t("google-workspace-integration.google-accordion.itemThree.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.google-accordion.itemFour.title",
          )}
        >
          <p>
            {t("google-workspace-integration.google-accordion.itemFour.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.google-accordion.itemFive.title",
          )}
        >
          <p>
            {t("google-workspace-integration.google-accordion.itemFive.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.google-accordion.itemSix.title",
          )}
        >
          <p>
            {t("google-workspace-integration.google-accordion.itemSix.text")}
          </p>
        </AccordionItem>
      </Accordion>

      <Accordion
        title={t("google-workspace-integration.benefits-accordion.title")}
        description={t(
          "google-workspace-integration.benefits-accordion.description",
        )}
      >
        <AccordionItem
          title={t(
            "google-workspace-integration.benefits-accordion.itemOne.title",
          )}
        >
          <p>
            {t("google-workspace-integration.benefits-accordion.itemOne.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.benefits-accordion.itemTwo.title",
          )}
        >
          <p>
            {t("google-workspace-integration.benefits-accordion.itemTwo.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.benefits-accordion.itemThree.title",
          )}
        >
          <p>
            {t(
              "google-workspace-integration.benefits-accordion.itemThree.text",
            )}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.benefits-accordion.itemFour.title",
          )}
        >
          <p>
            {t("google-workspace-integration.benefits-accordion.itemFour.text")}
          </p>
        </AccordionItem>
        <AccordionItem
          title={t(
            "google-workspace-integration.benefits-accordion.itemFive.title",
          )}
        >
          <p>
            {t("google-workspace-integration.benefits-accordion.itemFive.text")}
          </p>
        </AccordionItem>
      </Accordion>

      <SplitPane
        assetAlignment="stacked"
        title={t("google-workspace-integration.free-crm.title")}
      >
        <p className="mb-8 p--lg">
          {t("google-workspace-integration.free-crm.text.paragraphOne")}
        </p>
        <p className="mb-8 p--lg">
          <Trans
            i18nKey="google-workspace-integration.free-crm.text.paragraphTwo"
            components={{
              PaidPlans: (
                <Link className="text-link text-link--bare" to="/signup/" />
              ),
            }}
          />
        </p>
      </SplitPane>

      <TryFreeCallToAction
        title={t("google-workspace-integration.call-to-action.title")}
      />

      <section className="mb-8">
        <div className="container-fluid">
          <div className="row center-xs">
            <div className="col-xs-12">
              <p className="mb-8 p--sm">
                <Trans
                  i18nKey="google-workspace-integration.footer"
                  components={{
                    UserDataPolicy: (
                      <a
                        className="text-link text-link--bare"
                        href="https://developers.google.com/workspace/workspace-api-user-data-developer-policy"
                      />
                    ),
                    LimitedUse: (
                      <a
                        className="text-link text-link--bare"
                        href="https://developers.google.com/workspace/workspace-api-user-data-developer-policy#limited-use"
                      />
                    ),
                  }}
                />
              </p>
            </div>
          </div>
        </div>
      </section>

      <Accordion className="mt-20" title={t("common.faq")}>
        <AccordionItem
          title={t("google-workspace-integration.faqs.one.question")}
        >
          <p>{t("google-workspace-integration.faqs.one.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.two.question")}
        >
          <p>{t("google-workspace-integration.faqs.two.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.three.question")}
        >
          <p>{t("google-workspace-integration.faqs.three.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.five.question")}
        >
          <p>{t("google-workspace-integration.faqs.five.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.six.question")}
        >
          <p>{t("google-workspace-integration.faqs.six.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.seven.question")}
        >
          <p>{t("google-workspace-integration.faqs.seven.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.eight.question")}
        >
          <p>{t("google-workspace-integration.faqs.eight.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.nine.question")}
        >
          <p>{t("google-workspace-integration.faqs.nine.answer")}</p>
        </AccordionItem>

        <AccordionItem
          title={t("google-workspace-integration.faqs.ten.question")}
        >
          <p>{t("google-workspace-integration.faqs.ten.answer")}</p>
        </AccordionItem>
      </Accordion>
    </MainLayout>
  )
}

export const query = graphql`
  query ($language: String) {
    ...Locales
    ...Integrations
  }
`

export default GoogleWorkspaceIntegrationsPage
